import { useContext, useEffect, useState } from 'react';
import {
  MarketSignalStreamAnalysis,
  MarketSignalStreamAnalysisTopic,
} from '@/modules/market-signals/model/market-signals/market-signal-stream-analysis';
import marketSignalApi from '@/modules/market-signals/api/market-signal-api';
import { useSubscription } from '@/core/messaging/export';

import dayjs, { Dayjs } from 'dayjs';
import { useTimer } from '@/shared/hooks/export';
import {
  MarketSignalTopicTooltip,
  topicFontSize,
} from '@/modules/market-signals/components/market-signals/topics/market-signal-topic-badge';
import { MarketSignalContext } from '@/modules/market-signals/contexts/market-signal-context';
import { OverlayTrigger } from 'react-bootstrap';

import styles from '../market-signals.module.css';
import MarketSignalAlarmBadge from '@/modules/market-signals/components/market-signals/alarms/market-signal-alarm-badge';
import { bottom } from '@popperjs/core';

export default function MarketSignalTopicBadges({
  selectedStream,
  onClick,
  onLoaded,
  showTooltips,
}: {
  selectedStream: string;
  showTooltips: boolean;
  onClick: (i: MarketSignalStreamAnalysisTopic) => void;
  onLoaded: (items: MarketSignalStreamAnalysisTopic[]) => void;
}) {
  const { analysis, setAnalysis, alarms, hoveredItem } = useContext(MarketSignalContext);
  const [dateUpdated, setDateUpdated] = useState<Dayjs>();
  const [expanded, setExpanded] = useState<boolean>(true);
  const [maxRelevance, setMaxRelevance] = useState<number>(1);

  useEffect(() => {
    if (selectedStream)
      marketSignalApi.getAnalysisByStream(selectedStream, 0).then(value => {
        for (const c of value.content) {
          if (c.topics.length > 0) {
            setAnalysis(c);
            break;
          }
        }
      });
  }, [selectedStream, setAnalysis]);

  useEffect(() => {
    if (analysis) {
      const ids: string[][] = [];

      analysis.topics
        .sort((a, b) => b.relevance - a.relevance)
        .forEach(value => {
          ids.push(value.ids);
        });

      const topics = analysis.topics.sort((a, b) => b.relevance - a.relevance);
      setMaxRelevance(topics[0].relevance);
      onLoaded(topics);
    }
  }, [analysis, onLoaded]);

  useEffect(() => {
    if (analysis) {
      setDateUpdated(dayjs(analysis.dateCreated));
    }
  }, [analysis]);

  useTimer(
    30_000,
    () => {
      if (analysis) setDateUpdated(dayjs(analysis.dateCreated));
    },
    false,
    'sent-analysis-date-update',
  );

  useSubscription<MarketSignalStreamAnalysis>('/topic/signals/analysis', message => {
    if (message.stream === selectedStream && message.topics.length > 0) setAnalysis(message);
  });
  //newly changed .map key
  return (
    <div className={'mb-1 pb-1 '}>
      <div className={'w-100 small d-flex flex-wrap gap-1 mb-1'}>
        <>
          {analysis?.topics
            .sort((a, b) => b.relevance - a.relevance)
            .slice(0, expanded ? 10 : 5)
            .map((value, index) => (
              <OverlayTrigger
                key={index}
                placement={bottom}
                overlay={injected =>
                  MarketSignalTopicTooltip({ ...injected, topic: value, hide: !showTooltips })
                }
              >
                <span
                  className={`${topicFontSize(value)} ${styles.topicTableRow} ${value.ids.includes(hoveredItem?._id || '') && 'bg-body-tertiary '} p-1  border border-1 flex-grow-1 rounded-1 text-center`}
                  style={{ fontSize: `${0.8 + (value.relevance / maxRelevance) * 0.3}em` }}
                  onClick={event => onClick(value)}
                >
                  <span>
                    <MarketSignalAlarmBadge
                      alarms={alarms?.filter(a => value.ids.includes(a.signalId))}
                    />
                  </span>
                  <span className={'ms-1'}>{value.text}</span>
                </span>
              </OverlayTrigger>
            ))}
        </>
      </div>

      <div className={'small d-flex justify-content-between '}>
        <div className={'small'}>
          {analysis && analysis.topics.length > 5 && (
            <>
              {expanded && (
                <a href={'#a'} onClick={event => setExpanded(false)}>
                  - Show Less
                </a>
              )}
              {!expanded && (
                <a href={'#a'} onClick={event => setExpanded(true)}>
                  + Show More
                </a>
              )}
            </>
          )}
        </div>

        <div className={'small text-muted'}>updated {dateUpdated?.fromNow()}</div>
      </div>
    </div>
  );
}
